<template>
	<div>
		<div v-if="initLoading"></div>
		<div v-else-if="isTicketResolved" class="centralize d-flex flex-column">
			<v-icon size="78" color="grey lighten-1">fas fa-check</v-icon>
			<p class="headline">Ticket resolved</p>
			<p>{{ ticketType }} ticket</p>
			<p>Requested by {{ ticketEmail }}</p>
			<p>{{ ticketDate }}</p>
			<v-btn tile text @click="$router.go(-1)"
				><v-icon class="mr-2" color="grey">fa-arrow-left</v-icon>Go back</v-btn
			>
		</div>
		<div v-else class="ticket-grid">
			<shared-list-selection hide-center class="ticket-box-combo">
				<!-- Room Assignment -->
				<template #left>
					<shared-list-box
						header="Room Assignment"
						header-icon="fas fa-building"
						:subtext="roomSubtext"
						:items="listRooms"
						enable-search
					>
						<template #list="{ items }">
							<loader-list v-if="!rooms.length" />
							<v-list-item
								v-for="item in items"
								v-else
								:key="item.id"
								dense
								inactive
							>
								<v-list-item-content>
									<v-list-item-title v-text="item.name" />
								</v-list-item-content>
								<v-list-item-action>
									<div>
										<v-list-item-action-text
											class="mx-1 action-text"
											:class="item.actionClass"
											v-text="item.actionText"
										/>
										<template v-if="!item.actionText">
											<v-icon
												v-if="showAddNewRoom"
												class="mx-1 action-text__green"
												x-small
												@click="setNewRoom(item)"
												>fa-plus</v-icon
											>
											<v-icon
												v-if="showAddCurrentRoom"
												class="mx-1 action-text__red"
												x-small
												@click="setCurrentRoom(item)"
												>fa-minus</v-icon
											>
										</template>
										<v-icon
											v-if="showUnlinkRoom(item)"
											class="mx-1 action-text__red"
											x-small
											@click="unlinkRoom(item)"
											>fa-link-slash</v-icon
										>
									</div>
								</v-list-item-action>
							</v-list-item>
						</template>
					</shared-list-box>
				</template>

				<!-- Scenes -->
				<template #right>
					<shared-list-box
						header="Scenes"
						header-icon="fas fa-running"
						:subtext="sceneSubtext"
						:items="scenes"
					>
						<template #header-action>
							<menu-default :items="sceneWizardList">
								<template #activator="{ on }">
									<v-btn class="align-self-center" x-small icon v-on="on"
										><v-icon small>fas fa-plus</v-icon></v-btn
									>
								</template>
							</menu-default>
						</template>

						<template #box="{ items }">
							<loader-list v-if="loadingScenes" />

							<div v-else-if="scenesHasError" class="centralize">
								<v-icon size="48">fas fa-exclamation-triangle</v-icon>
							</div>

							<!-- scene item row -->
							<v-list-item v-for="item in items" v-else :key="item.id" dense>
								<v-list-item-content>
									<v-list-item-title v-text="item.name" />
								</v-list-item-content>

								<!-- scene row menu -->
								<v-list-item-action class="my-0">
									<menu-default
										icon="fas fa-ellipsis-v"
										:items="itemOptions(item, 'scene')"
									/>
								</v-list-item-action>
							</v-list-item>
						</template>
					</shared-list-box>
				</template>
			</shared-list-selection>

			<shared-list-selection hide-center class="ticket-box-combo">
				<!-- Personal Devices -->
				<template #left>
					<shared-list-box
						header="Personal Devices"
						header-icon="fas fa-bed"
						:items="personalDevices"
					>
						<template #header-action>
							<v-btn
								class="align-self-center"
								x-small
								icon
								@click="popAssocDialog()"
								><v-icon small>fas fa-plus</v-icon></v-btn
							>
						</template>

						<template #box="{ items }">
							<loader-list v-if="!resident" />
							<v-list-item
								v-for="item in items"
								v-else
								:key="item.id"
								dense
								inactive
							>
								<v-list-item-content>
									<v-list-item-title class="personal-item">
										<span class="text-truncate">{{ item.name }}</span>
										<span class="text-truncate">{{ item.identifier }}</span>
									</v-list-item-title>
								</v-list-item-content>

								<v-list-item-action class="my-0">
									<menu-default
										icon="fas fa-ellipsis-v"
										:items="itemOptions(item, 'personalDevice')"
									/>
								</v-list-item-action>
							</v-list-item>
						</template>
					</shared-list-box>
				</template>

				<!-- Ticket Details -->
				<template #right>
					<div class="ticket-info ml-2">
						<p class="title ml-2">Ticket Details</p>
						<p>{{ ticketType }} ticket</p>
						<p>{{ community.name }}</p>
						<p>Requested by {{ ticketEmail }}</p>
						<p v-if="ticketBody">{{ ticketBody }}</p>
						<p>{{ ticketDate }}</p>
						<v-alert v-if="resolveError" type="error" outlined dismissible>
							{{ resolveError }}
						</v-alert>
						<!-- <a href="#" target="_blank" rel="noopener noreferrer">Link</a> -->
						<div class="ticket-actions">
							<v-btn tile text :to="{ name: 'K4NOC Room Moves' }">Cancel</v-btn>
							<v-btn
								outlined
								color="ocean darken-1"
								:loading="resolveLoading"
								@click="resolveTicket"
								>Mark Complete</v-btn
							>
						</div>
					</div>
				</template>
			</shared-list-selection>

			<!-- scene wizard dialogs -->
			<wizard-scene-dialog ref="WizardDialog" />

			<dialog-confirm-dialog
				ref="confirmRemoveSceneDialog"
				:body="sceneStrings.removeSceneConfirmation"
			/>

			<wizard-scene-dialog-wrapper ref="WizardWrapperDialog" />

			<!-- personal devices dialogs -->
			<personal-device-dialog ref="personalDeviceDialog" />

			<dialog-confirm-dialog
				ref="confirmRemoveDeviceDialog"
				:body="strings.k4noc.moves.dialog.devices.remove"
			/>

			<!-- confirm complete -->
			<dialog-confirm-dialog
				ref="confirmMarkComplete"
				:body="strings.k4noc.moves.dialog.markComplete"
			/>
		</div>
	</div>
</template>

<script>
import _ from "lodash";
import { mapGetters, mapActions } from "vuex";
import store from "@/store";
import strings from "@/utils/strings";

import PersonalDeviceDialog from "./dialogs/PersonalDevice.vue";

export default {
	components: {
		PersonalDeviceDialog,
	},

	data() {
		return {
			initLoading: false,
			resolveLoading: false,
			resolveError: "",
			originalAddr: {
				newAddress: "",
				currentAddress: "",
			},
			sceneStrings: strings.live.scenes,
			strings,
		};
	},

	computed: {
		...mapGetters({
			ticket: "tickets/roomMoves/current",
			resident: "tickets/roomMoves/resident",
			personalDevices: "tickets/roomMoves/personalDevices",
			rooms: "rooms/rooms",
			roomByName: "rooms/roomByName",
			_scenes: "k4/scenes/scenes",
			wizards: "k4/scenes/wizard/availableWizards",
			controllerStatus: "controller/state",
			controllerStates: "controller/states",
			getCommunity: 'communities/itemById'
		}),

		scenes()
		{
			return _.map(this._scenes);
		},

		roomSubtext() {
			const { moveType, newAddress, currentAddress } = this.ticket;
			const subtexts = _.get(
				strings.k4noc.moves.subtext.room,
				moveType,
				() => ""
			);
			return subtexts([currentAddress, newAddress]);
		},

		listRooms() {
			const { newAddress, currentAddress } = this.ticket;
			return _.chain(this.rooms)
				.map((_room) => {
					const room = { ..._room };
					if (room.name === newAddress) {
						this.setNewRoom(room);
					}
					if (room.name === currentAddress) {
						this.setCurrentRoom(room);
					}
					return room;
				})
				.sortBy(["actionText", "name"])
				.value();
		},

		loadingScenes() {
			const states = this.controllerStates;
			return (
				this.sceneRoom.id &&
				[states.UNKNOWN, states.DISCOVERED].includes(this.controllerStatus)
			);
		},

		scenesHasError() {
			const states = this.controllerStates;
			return (
				!this.sceneRoom.id ||
				[states.NOT_AVAILABLE, states.NOT_FOUND].includes(this.controllerStatus)
			);
		},

		sceneSubtext() {
			if (this.scenesHasError)
				return strings.k4noc.moves.subtext.sceneError(this.sceneRoom.name);
			return strings.k4noc.moves.subtext.scene(this.sceneRoom.name);
		},

		community() {
			return this.getCommunity(this.ticket?._communityId)
		},

		sceneRoom() {
			const { newAddress, currentAddress } = this.ticket;
			const room = newAddress || currentAddress;
			return this.roomByName(room) || { name: room };
		},

		showAddNewRoom() {

			const { newAddress, currentAddress } = this.ticket;

			return (
				!this.roomByName(newAddress) &&
				this.ticketType !== "Move Out"
			);
		},

		showAddCurrentRoom() {

			const { newAddress, currentAddress } = this.ticket;

			return (
				!this.roomByName(currentAddress) &&
				this.ticketType !== "Move In"
			);
		},

		ticketType() {
			return (this.ticket) ? _.startCase(this.ticket._type) : '';
		},



		ticketEmail() {
			return _.get(this.ticket, "_email");
		},
		
		ticketBody() {
			return _.get(this.ticket, "_body");
		},

		ticketDate() {
			const date = new Date(this.ticket._created);
			const options = {
				dateStyle: "medium",
				timeStyle: "short",
			};
			return Intl.DateTimeFormat("en-US", options).format(date);
		},

		sceneWizardList() {
			const list = [
				{
					title: "Scene Wizards",
					props: {
						disabled: true,
					},
				},
			];
			list.push(
				...this.wizards
					.filter(wiz => wiz.toLowerCase() !== "advanced")
					.map((wizard) => ({
						title: this.sceneStrings.wizards.add[wizard],
						action: () => this.popWizard(wizard),
						props: {
							link: true,
						},
					}))
			);

			return list;
		},

		isTicketResolved() {
			
			return this.ticket._status === 'closed';
		},
	},

	watch: {
		"sceneRoom.id"(roomId, old) {
			if (roomId) {
				this.loadController(roomId);
			} else {
				this.resetController();
			}
		},
	},

	async created() {

		const { roomMove, moveIn, moveOut, _communityId, _operatorId } = this.ticket;
		const { _to, _from } = roomMove || moveIn || moveOut;

		await this.$store.dispatch('session/setCommunityId', { id: _communityId, operator: _operatorId });

		this.ticket.newAddress = _to;
		this.ticket.currentAddress = _from;
		this.originalAddr.newAddress = _to;
		this.originalAddr.currentAddress = _from;
	},

	methods: {
		...mapActions({
			loadRooms: "rooms/load",
			resetController: "controller/reset",
			loadController: "controller/loadRemote",
			loadDevices: "devices/load",
			getResident: "tickets/roomMoves/getResident",
		}),

		itemOptions(item, type) {
			let edit = () => this.popAssocDialog(item);
			let remove = () => this.confirmRemoveDevice(item);
			if (type === "scene") {
				edit = () => this.popWrapper(item);
				remove = () => this.confirmRemoveScene(item);
			}

			return [
				{
					title: "Edit",
					action: edit,
				},
				{
					title: "Delete",
					action: remove,
				},
			];
		},

		setNewRoom(room) {
			room.actionText = "Assigning To";
			room.actionClass = "action-text__green";
			this.$store.commit("tickets/roomMoves/SET_CURRENT", {
				...this.ticket,
				newAddress: room.name,
			});
		},

		setCurrentRoom(room) {
			if (this.ticketType === "Move") {
				room.actionText = "Removing";
				room.actionClass = "action-text__red";
			} else {
				room.actionText = "Current";
				room.actionClass = "action-text__orange";
			}
			this.$store.commit("tickets/roomMoves/SET_CURRENT", {
				...this.ticket,
				currentAddress: room.name,
			});
		},

		unlinkRoom(room) {
			let roomType =
				room.actionClass === "action-text__green"
					? "newAddress"
					: "currentAddress";
			delete room.actionText;
			delete room.actionClass;
			this.$store.commit("tickets/roomMoves/SET_CURRENT", {
				...this.ticket,
				[roomType]: this.originalAddr[roomType],
			});
		},

		showUnlinkRoom(room) {
			if (!room.actionClass) return false;
			let roomType =
				room.actionClass === "action-text__green"
					? "newAddress"
					: "currentAddress";
			return room.name !== this.originalAddr[roomType];
		},

		popWizard(wizard) {
			this.$store.dispatch("descriptions/load");
			this.$refs.WizardDialog.pop(wizard);
		},

		popWrapper(scene) {
			this.$refs.WizardWrapperDialog.pop(scene);
			// disclaimer: changes made might take a while to reflect in subsequent changes
		},

		popAssocDialog(association = undefined) {
			const action = `tickets/roomMoves/${
				association ? "update" : "add"
			}PersonalDevice`;

			this.$refs.personalDeviceDialog.pop(
				(params) => this.$store.dispatch(action, params),
				_.clone(association)
			);
		},

		async confirmRemoveScene(scene) {
			let confirmed = await this.$refs.confirmRemoveSceneDialog.pop();
			if (!confirmed) return;

			await this.$store.dispatch("k4/scenes/removeScene", scene.name);
		},

		async confirmRemoveDevice(association) {
			let confirmed = await this.$refs.confirmRemoveDeviceDialog.pop();
			if (!confirmed) return;

			await this.$store.dispatch(
				"tickets/roomMoves/removePersonalDevice",
				association
			);
		},

		async resolveTicket() {
			let confirmed = await this.$refs.confirmMarkComplete.pop();
			if (!confirmed) return;
			this.resolveLoading = true;

			const data = {
				oldSys: this.roomByName(this.ticket.currentAddress),
				newSys: this.roomByName(this.ticket.newAddress),
			};

			try {
				await this.$store.dispatch(
					`tickets/roomMoves/close-${this.ticketType}`,
					data
				);

				await this.$store.dispatch("tickets/roomMoves/closeTicket");
			} catch (e) {
				this.resolveError = e.message;
				throw e;
			} finally {
				this.resolveLoading = false;
			}
		},
	},

	async beforeRouteEnter(to, from, next) {
		try {
			let current = store.getters["tickets/roomMoves/current"];

			if (!current) {
				const items = await store.dispatch("tickets/roomMoves/getItems");
				current = items.find((ticket) => ticket._id === to.params.ticketId);
				store.commit("tickets/roomMoves/SET_CURRENT", current);
			}

			const _owner = current?._owner;

			to.meta.title = (_owner && typeof _owner == 'object') ? `${ _owner.preferredName || _owner.firstName} ${_owner.lastName}` : 'Unknown';

			next(async (vm) => {
				vm.initLoading = true;
				await vm.getResident(to.params.ticketId);

				if (vm.community) {
					await vm.loadRooms(vm.community);
				}
				vm.initLoading = false;
			});
		} catch (e) {
			next({ name: "K4NOC Room Moves" });
			throw e;
		}
	},
};
</script>

<style lang="scss" scoped>
.ticket-grid {
	> * {
		padding: 12px 16px;
		margin-bottom: 12px;
	}
}

.centralize {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
}

.action-text {
	font-size: 0.65rem;
	letter-spacing: 0.0333333em;
	text-transform: uppercase;
	user-select: none;

	&__green {
		color: var(--v-ocean-base) !important;
	}

	&__orange {
		color: var(--v-orange-base) !important;
	}

	&__red {
		color: var(--v-rust-red-base) !important;
	}
}

.ticket-box-combo {
	position: relative;
	height: calc(50vh - 60px);
}

.ticket-info {
	min-height: 50%;

	> p {
		margin-bottom: 8px;
	}

	.ticket-actions {
		position: absolute;
		bottom: -20px;
		right: 0;
		padding: 8px;
		display: flex;
		gap: 4px;
	}
}

i.fa-link-slash:before {
	content: "\f127";
}

.personal-item {
	display: flex;

	> span {
		width: 50%;
	}
}

@media (max-width: 730px) {
	.ticket-box-combo {
		height: 80vh;
	}

	.ticket-info .ticket-actions {
		bottom: 0;
	}
}
</style>
